import React, { useContext, useEffect, useRef, useState } from "react";
import { GridLoader } from "../../rcsgrid/createGridTools";
import { localization } from "../../Tools/Tools";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";
import { createGrid, createRecordSource } from "../../rcsgrid/rcsgrid";
import { DefaultDialogContentProps } from "./DialogAcctools";
import { resumeRequest } from "../../../TokenProcessing/TokenProcessing";
import { SmetaModalsButtonsContext } from "../../Tools/modalManager";
import { Prefixes } from "../../Tools/ProjectX";
import smetaAxios from "../../../SmetaAxios";

interface KladrProps {
  ObjType?: string;
  RequestID?: string;
  data: DefaultDialogContentProps;
  func: (data: any) => void;
}

/**
 * Получение данных для выбранного элемента
 *
 * @param {Object} [request] объект с данными для запроса
 * @param {string} [requestId] id цепочки запросов
 *
 * @returns Promise<{}>
 */
const getSelectData = (request: any, requestId?: string) => {
  const params = {
    RequestID: requestId,
    WSM: "1",
  };

  const config = {
    prefix: Prefixes.default,
    method: "POST",
    url: "ResumeRequest",
    data: request,
    params,
  };

  return smetaAxios(config);
};

/**
 * Запрос установки фильтра для таблицы
 *
 * @param {string} [filter] строка фильтра
 * @param {string} [objType] тип объекта
 *
 */
const filtration = (filter: string, objType?: string) => {
  const params = {
    ObjType: objType,
    Filter: filter,
  };
  const config = {
    prefix: Prefixes.kladr,
    method: "GET",
    url: "setFilter",
    params,
  };

  return smetaAxios(config);
};

/**
 * Контент диалога с таблицей адресов из кладр
 *
 * Поля компонента:
 *    1. [mainGrid, setMainGrid] - управление состоянием таблицы
 *    2. [timer, setTimer] - таймер фильтрации для экономиии трафика
 *    3. gridpanelRef - ссылка на таблицу
 *
 * @param {Object} [props] объект с данными для компонента диалога адресов
 * @param {string} [props.ObjType] тип объекта
 * @param {string} [props.RequestID] id цепи запросов
 * @param {Object} [props.data] объект с данными от модуля инициатора
 * @param {Object} [props.data.FieldName] имя редактируемого поля
 * @param {Object} [props.data.onEdit] функция на редактирование
 * @param {Object} [props.data.ObjRef] id выбранного элемента
 *
 * @returns Возвращает контент диалога с таблицей адресов
 */
export default function DialogKldr(props: KladrProps) {
  const { ObjType: objType, RequestID: requestId, data } = props;
  const { props: dataProps } = data;

  const [mainGrid, setMainGrid] = useState<{ grid: createGrid; source: createRecordSource }>();
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const setButtons = useContext(SmetaModalsButtonsContext);

  const gridpanelRef = useRef<HTMLDivElement>(null);

  const initGrid = async () => {
    setMainGrid(await GridLoader(gridpanelRef.current, props));
  };
  useEffect(() => {
    initGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data.props.hiddenButton) return;
    const modalButtons: { [key: number]: { label: string; onClick?: () => void } } = {};
    if (!data.props.Buttons || Number(data.props.Buttons) & 1) {
      modalButtons[1] = { label: "Установить", onClick: onSelect };
      modalButtons[2] = { label: "Закрыть", onClick: onCancel };
    }
    setButtons?.(modalButtons);
  }, [setButtons, mainGrid, dataProps]);

  /**
   * Получить объект для запроса данных о классификаторе
   *
   * @return Объект со всеми данными классификатора
   */
  const getRequestJson = () => {
    if (!mainGrid || !dataProps.FieldName) return;
    const code = mainGrid.source.getFieldTextSync("Code");
    const name = mainGrid.source.getFieldTextSync("Name");
    const short = mainGrid.source.getFieldTextSync("Socr");
    const GUID = mainGrid.source.getFieldTextSync("GUID");
    if (dataProps.FieldName === "BulkNo" || dataProps.FieldName === "BuildNo") {
      dataProps.FieldName = "House";
    }

    return {
      Result: "1",
      Code: code,
      Classificator: { Классификатор: code },
      [dataProps.FieldName]: {
        [localization()[dataProps.FieldName]]: name,
        Сокращение: short,
        Код: code[0] + "" + code[1],
      },
      GUID: { Идентификатор: GUID },
    };
  };

  /**
   * Устанавливает новый классификатор
   */
  const onSelect = async () => {
    const json = await getSelectData(getRequestJson(), requestId);
    if (json.NeedRefresh === "1") {
      props.func?.(json);
      return;
    }
    dataProps.onEdit?.({
      kladr: true,
      json: json,
      objref: dataProps.ObjRef,
    });
  };

  /**
   * Закрывает без изменений
   */
  const onCancel = () => {
    resumeRequest(requestId, data, {});
  };

  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.dialogKldrSearchContainer)}>
        <div className={clsx(dialogStyles.dialogKldrSearchLabel)}>Поиск:</div>
        <input
          className={clsx(dialogStyles.dialogKldrSearchInput)}
          onChange={(ev) => {
            clearTimeout(timer);

            setTimer(
              setTimeout(async () => {
                await filtration(ev.target.value === undefined ? "" : ev.target.value, objType);
                if (mainGrid) await mainGrid.grid.refreshSource();
              }, 200),
            );
          }}
          title="Filter"
        />
      </div>
      <div className={clsx(dialogStyles.fullDialogFlexContainer)}>
        <div
          id="rcgrid_panel"
          ref={gridpanelRef}
          style={{
            position: "relative",
            height: "100%",
            width: "100%",
          }}
        ></div>
      </div>
    </div>
  );
}
